import { graphql } from '@/gql'

export const USER_QUERY = graphql(`
  query getUser($id: UUID!) {
    userById(id: $id) {
      id
      firstName
      lastName
      fullName
      email
      company {
        id
        name
      }
    }
  }
`)

export const USER_NAMES_BY_IDS = graphql(`
  query userNamesByIds($ids: [UUID!]) {
    users(filter: { id: { in: $ids } }) {
      nodes {
        id
        firstName
        lastName
        fullName
      }
    }
  }
`)

export const USER_BY_ID = graphql(`
  query getUserById($id: UUID!) {
    userById(id: $id) {
      id
      fullName
      email
      logo
      departments
      canSignOnBehalfOf
      company {
        id
        name
      }
      userOnBehalfPermissions {
        nodes {
          id
          onBehalfOfUserId
        }
      }
    }
  }
`)

export const USERS_BY_IDS = graphql(`
  query usersByIds($ids: [UUID!], $orderBy: [UsersOrderBy!]) {
    users(filter: { id: { in: $ids } }, orderBy: $orderBy) {
      nodes {
        id
        fullName
        email
        emailWork
        logo
        departments
        canSignOnBehalfOf
        company {
          id
          name
        }
        userOnBehalfPermissions {
          nodes {
            id
            onBehalfOfUserId
          }
        }
      }
    }
  }
`)

export const USERS_INPUT_FIELD_QUERY = graphql(`
  query usersInputField(
    $companyId: UUID
    $selected: [UUID!]
    $search: String
    $after: Cursor
    $orFilter: [UserFilter!]
    $withEmployeeId: Boolean = false
    $departmentsFilter: UUIDListFilter
    $filterByIds: UUIDFilter
  ) {
    allUsers: users(
      first: 20
      condition: { active: true }
      orderBy: [FIRST_NAME_ASC, LAST_NAME_ASC, ID_ASC]
      filter: {
        isArchived: { equalTo: false }
        companyId: { equalTo: $companyId }
        fullName: { includesInsensitive: $search }
        id: $filterByIds
        departments: $departmentsFilter
        or: $orFilter
      }
      after: $after
    ) {
      edges {
        node {
          id
          employeeId @include(if: $withEmployeeId)
          logo
          fullName
          canSignOnBehalfOf
          userDepartments {
            edges {
              node {
                departmentId
                department {
                  id
                  name
                }
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
    }
    selectedUsers: users(
      condition: { active: true }
      orderBy: [FIRST_NAME_ASC, LAST_NAME_ASC, ID_ASC]
      filter: {
        companyId: { equalTo: $companyId }
        or: [{ id: { in: $selected } }, { employeeId: { in: $selected } }]
      }
    ) {
      edges {
        node {
          id
          employeeId @include(if: $withEmployeeId)
          logo
          fullName
          userDepartments {
            edges {
              node {
                department {
                  id
                  name
                }
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
    }
  }
`)

export const USERS_INPUT_FIELD_SEARCH_QUERY = graphql(`
  query usersInputFieldSearch(
    $companyId: UUID
    $selected: [UUID!]
    $search: String
    $after: Cursor
    $orFilter: [UserFilter!]
    $withEmployeeId: Boolean = false
    $departmentsFilter: UUIDListFilter
    $filterByIds: UUIDFilter
  ) {
    allUsers: usersSearchName(
      argSearchString: $search
      first: 20
      condition: { active: true }
      orderBy: [FIRST_NAME_ASC, LAST_NAME_ASC, ID_ASC]
      filter: {
        isArchived: { equalTo: false }
        companyId: { equalTo: $companyId }
        id: $filterByIds
        or: $orFilter
        departments: $departmentsFilter
      }
      after: $after
    ) {
      edges {
        node {
          id
          employeeId @include(if: $withEmployeeId)
          logo
          fullName
          canSignOnBehalfOf
          userDepartments {
            edges {
              node {
                departmentId
                department {
                  id
                  name
                }
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
    }
    selectedUsers: users(
      condition: { active: true }
      orderBy: [FIRST_NAME_ASC, LAST_NAME_ASC, ID_ASC]
      filter: { companyId: { equalTo: $companyId }, id: { in: $selected } }
    ) {
      edges {
        node {
          id
          employeeId @include(if: $withEmployeeId)
          logo
          fullName
          userDepartments {
            edges {
              node {
                department {
                  id
                  name
                }
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
    }
  }
`)

export const DIRECT_MANAGERS = graphql(`
  query directManagers($orderBy: [UsersOrderBy!]) {
    directManagers(orderBy: $orderBy) {
      edges {
        node {
          id
          fullName
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
`)

export const ALL_DEPARTMENT_LEADERS = graphql(`
  query allDepartmentLeaders($companyId: UUID!, $orderBy: [UsersOrderBy!]) {
    users(
      condition: {
        companyId: $companyId
        active: true
        isResigned: false
        isLeader: true
      }
      orderBy: $orderBy
    ) {
      edges {
        node {
          id
          fullName
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
`)

export const DEPARTMENT_LEADERS = graphql(`
  query getDepartmentLeaders($departmentId: UUID!) {
    getDepartmentLeaders(departmentId: $departmentId) {
      nodes {
        id
        fullName
      }
    }
  }
`)

export const DEPARTMENT_LEADERS_COUNT = graphql(`
  query getDepartmentLeadersCount($departmentId: UUID!) {
    getDepartmentLeadersCount(departmentId: $departmentId)
  }
`)

export const USERS_DEPARTMENTS = graphql(`
  query allUsersDepartments($companyId: UUID!, $employeeIds: [UUID!]!) {
    userDepartments(
      filter: {
        companyId: { equalTo: $companyId }
        department: { active: { equalTo: true } }
        user: {
          active: { equalTo: true }
          employeeByUserId: { id: { in: $employeeIds } }
        }
      }
    ) {
      nodes {
        userId
        rankId
        department {
          id
          name
        }
      }
    }
  }
`)

export const USER_SETTING_BY_KEY = graphql(`
  query userSettingByKey($userId: UUID!, $key: UserSettingKey!) {
    userSettingByUserIdAndKey(userId: $userId, key: $key) {
      id: key
      value
      userId
    }
  }
`)

export const VALIDATE_EMAIL_AVAILABILITY = graphql(`
  query validateEmailAvailability($userId: UUID, $email: String) {
    validateEmailAvailability(userId: $userId, email: $email)
  }
`)

export const USERS_BASIC_QUERY = graphql(`
  query usersBasic($filter: UserFilter) {
    users(
      first: 1
      orderBy: [FIRST_NAME_ASC, LAST_NAME_ASC, ID_ASC]
      condition: { active: true }
      filter: $filter
    ) {
      nodes {
        id
        firstName
        lastName
        fullName
      }
    }
  }
`)

export const USERS_FILTER = graphql(`
  query usersFilter($companyId: UUID!) {
    users(
      orderBy: [FIRST_NAME_ASC, LAST_NAME_ASC, ID_ASC]
      condition: { active: true, companyId: $companyId }
    ) {
      edges {
        node {
          id
          fullName
        }
      }
    }
  }
`)

export const SUPER_USER_LOGIN_NOTES_QUERY = graphql(`
  query superUserLoginNotes(
    $userId: UUID!
    $orderBy: [SuperUserLoginNotesOrderBy!]
    $first: Int
    $after: Cursor
  ) {
    superUserLoginNotes(
      orderBy: $orderBy
      first: $first
      after: $after
      filter: { userId: { equalTo: $userId } }
    ) {
      edges {
        node {
          id
          note
          createdAt
          superUser {
            fullName
            logo
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`)
